import { z } from 'zod';
import { OauthServiceType, BaggageType, OrderAdditionalServiceGdsServiceServiceType } from '@websky/graphql';
import { Locale, StepsEnum } from '../types';
import { BooleanOptional, NumberOptional, StringOptional } from './schemaTypes';

export const UpsaleStrategySchema = z
	.enum(['checkout-insurance-step', 'checkout-order-service', 'checkout-before-payment', 'checkin-insurance-step'])
	.nullish();

export const LoyaltyInfoLinkSchema = z.object({
	url: z.string(),
	title: z.string()
});

const LoyaltyInfoSchema = z.object({
	redirectTo: StringOptional,
	links: z.array(LoyaltyInfoLinkSchema).optional()
});

const SocialAuthMethodSchema = z.object({
	service: z.nativeEnum(OauthServiceType),
	isAvailable: z.boolean()
});

export const SavePreselectedWithoutUserAction = z
	.object({
		types: z.array(z.nativeEnum(OrderAdditionalServiceGdsServiceServiceType))
	})
	.describe(
		`
		настройка определяет, какие типы услуг сохранять в корзине без вывода дополнительной модалки допродажи
		по умолчанию, сервисы всех типов выводятся в модалке допродажи
		не работает на страховки, там отдельная логика UpsaleInsurance/hook
		и местах по той же причине
	`
	)
	.default({ types: [] });

export const CompanyInfoSchema = z
	.object({
		iataCode: z.string().describe('иата код авиакомпании, на него много чего подвязано в коде'),
		ticketNumberCode: StringOptional,
		privacyPolicy: StringOptional,
		facebook: StringOptional,
		instagram: StringOptional,
		vk: StringOptional,
		twitter: StringOptional,
		webskySearchOrder: StringOptional,
		exareRulesURL: StringOptional,
		exarePrivacyPolicyURL: StringOptional,
		legal: StringOptional,
		meal: StringOptional,
		seat: StringOptional,
		baggage: StringOptional,
		insurance: StringOptional,
		loyaltyProgramName: StringOptional,
		loyaltyProgramRegistrationUrl: StringOptional.describe(
			'ссылка на регистрацию в программе лояльности на сайте ак\nдолжна быть указана даже если не используется\n\nв коде на нее есть завязанная логика например\nWEBSKY-4511'
		),
		loyaltyProgramAccountUrl: StringOptional,
		loyaltyProgramRulesUrl: StringOptional.describe(
			'Ссылка на правила программы лояльности. Используется в чекбоксе в форме регистрации ффп в модалке'
		),
		loyalty: LoyaltyInfoSchema.optional(),
		multiInsurance: BooleanOptional,
		specialDocumentForInsurance: BooleanOptional,
		passengerConductRules: StringOptional,
		baggageAnimalsURL: StringOptional,
		subsidyRulesUrl: StringOptional,
		subsidyFaresConditionURL: StringOptional,
		transferM2ConditionURL: StringOptional,
		farelockURL: StringOptional.describe('Ссылка на правила в плашке заморозки цены'),
		refundOptionURL: StringOptional.describe('Ссылка на правила в плашке услуги возврата'),
		upgradeOptionURL: StringOptional,
		transferBaggageUrl: StringOptional,
		hotelsURL: StringOptional
	})
	.superRefine((data, ctx) => {
		if (
			data.loyaltyProgramName?.trim() &&
			(!data.ticketNumberCode?.trim() || data.ticketNumberCode?.trim() === '00Z')
		) {
			ctx.addIssue({
				code: 'custom',
				path: ['ticketNumberCode'],
				message: 'ticketNumberCode must be provided when loyaltyProgramName is set.'
			});
		}
	});

const ServicesAvailableSchema = z.object({
	animal: BooleanOptional
});

const StepsAvailableSchema = z.record(z.nativeEnum(StepsEnum), BooleanOptional);

const SeatMapConfigSchema = z.object({
	splitByCabin: BooleanOptional,
	showRecommendedSeats: BooleanOptional,
	showFilters: BooleanOptional.describe('Отображать фильтры для пассажиров на карте мест'),
	showPriceInSeat: BooleanOptional,
	collapseEmptySeats: BooleanOptional.describe(
		'Скрывать несуществующие места внутри ряда, краевые оставлять видимыми'
	)
});

const UpsaleServicesItemSchema = z.object({
	rfisc: z.string(),
	description: StringOptional,
	icon: z.any().optional(),
	title: StringOptional,
	extraTitle: StringOptional,
	extraTitleShowDates: z.array(z.string()).optional()
});

const UpsaleServicesConfigSchema = z.record(z.array(UpsaleServicesItemSchema)).optional();

export const ReserveEnginesSchema = z
	.object({
		redirectToWebsky1: BooleanOptional,
		checkin: StringOptional,
		avia: StringOptional,
		siteUrl: StringOptional
	})
	.superRefine((data, ctx) => {
		if (data.redirectToWebsky1 && !data.checkin?.trim()) {
			ctx.addIssue({
				code: 'custom',
				path: ['checkin'],
				message: 'Checkin URL must be provided when redirectToWebsky1 is true.'
			});
		}
	});

const baggageByTypeLimitSchema = z.object({
	baggageType: z.nativeEnum(BaggageType).describe('тип багажа (string, например "PetInCabin")'),
	maxBaggagePerPassenger: z.number().int().nonnegative().describe('максимальное кол-во багажа этого типа')
});

const baggageLimitSchema = z.object({
	baggageByTypeLimit: z.array(baggageByTypeLimitSchema).optional(),
	maxBaggagePerPassenger: z.number().int().nonnegative().optional().describe('общее максимальное кол-во багажа')
});

export const baggageConfigSchema = z.object({
	baggageLimit: baggageLimitSchema.optional()
});

const AdditionalResourcesSchema = z.object({
	svgs: z.array(z.string().url()).optional(),
	extraSvgs: z.array(z.string().url()).optional(),
	scripts: z.array(z.string().url()).optional(),
	extraScripts: z.array(z.string().url()).optional()
});

const FareGroupVariantSchema = z.enum(['small', 'big']);

export const GlobalConfigSchema = z.object({
	savePreselectedWithoutUserAction: SavePreselectedWithoutUserAction.optional(),
	socialAuthMethods: z.array(SocialAuthMethodSchema).optional(),
	authMethods: z
		.object({
			email: BooleanOptional,
			phone: BooleanOptional,
			ffp: BooleanOptional,
			blockedRegistrationEmailDomains: z.array(z.string()).optional()
		})
		.optional(),

	/**
	 * Блок настроек метрики:
	 *
	 * WEBSKY-7938: collectMetrics является общим рубильником для всех метрик
	 * Если collectMetrics = false, принудительно устанавливаем:
	 * - usePixel = false
	 * - useYandexMetrika = false
	 * - useSberAds = false
	 */
	collectMetrics: BooleanOptional.default(true).describe('собирать метрики на проекте или нет'),

	usePixel: BooleanOptional.default(false).describe('использовать ли @websky/pixel модуль внутри приложения'),
	useYandexMetrika: BooleanOptional.default(true).describe('использовать ли Яндекс Метрику'),
	useSberAds: BooleanOptional.default(true).describe('использовать ли Сбер Метрику'),

	yandexMetrikaId: z
		.union([z.string(), z.array(z.string())])
		.optional()
		.describe('ID счетчика Яндекс.Метрики.'),
	sberAdsId: StringOptional,
	/** Конец блока настроек метрики */

	varioqubId: StringOptional,
	findOrderURL: StringOptional.describe('@deprecated'),
	gtmId: StringOptional.describe('Google Tag Manager ID'),
	fareGroupVariant: FareGroupVariantSchema.optional().describe('вариант карточки выбора бренда для перелета'),

	showIsActualizationResultOk: z.boolean(),
	allowPromoCodes: BooleanOptional.describe('Включение поддержки промокодов на форме поиска'),
	showZeroCharges: BooleanOptional.describe(
		'Отображать на странице оплаты в детализации комиссии и сборы с нулевой стоимостью'
	),
	maxBaggage: NumberOptional,
	dynamicBaggagePricing: BooleanOptional.describe(
		'запрещает выбирать несколько типов сумок (CheckedBaggage); отправляет запрос на модификацию при каждом изменении багажа (WEBSKY-3377)'
	),
	maxBaggagePerPassenger: NumberOptional.describe(
		'Максимальное количество единиц багажа, которое может добавить один пассажир'
	),
	baggage: baggageConfigSchema.optional().describe('См. BaggageConfig'),
	seatMap: SeatMapConfigSchema.optional(),
	insurance: z
		.object({
			upsaleStrategy: z.array(UpsaleStrategySchema).nonempty(),
			allowImplicitConfirm: BooleanOptional.describe(
				'Настройка неявного подтверждения. Если включена, то отображаем ошибку в радио кнопках на широких страховках (important)'
			),
			multiplePriorityInsurances: BooleanOptional.describe(
				'Настройка множественных приоритетных страховок. Если включена, то предлагаем приоритетные страховки отсортированные по salePriority (игнорируя уже выбранные приоритетные страховки)'
			),
			insurancePriorities: z
				.array(z.string())
				.optional()
				.describe(
					'Показывает порядок выводимых страховок на странице InsurancePage. В массиве так же можно указывать один элемент, если нужно отобразить только одну страховку первой. Коды хранятся в поле InsuranceProgram.code'
				)
		})
		.optional(),
	checkoutAdditionalService: z.any().optional().describe('См. CheckoutAdditionalServiceConfig'),
	locale: z.nativeEnum(Locale).optional().describe('Язык'),
	apiUrl: StringOptional.describe('Адрес схемы GraphQL запросов'),
	page404Url: StringOptional.describe(
		'Страница, отображаемая при ошибке 404 (неверный адрес). Если не задана - отображается дефолтная страница 404 движка'
	),
	i18n: z.any().optional().describe('Переводы, задаваемые в конфиге'),
	companyInfo: CompanyInfoSchema.optional().describe('Параметры компании'),
	FFPRegex: z
		.instanceof(RegExp)
		.optional()
		.describe('Регулярное выражение для валидации ввода номера карты лояльности'),
	reserveEngines: ReserveEnginesSchema.optional().describe('ссылки на предыдущие версии модулей'),
	regex: z
		.object({
			PNR_OR_TICKET_NUMBER_REGEX: z.instanceof(RegExp).optional(),
			TICKET_NUMBER_REGEX: z.instanceof(RegExp).optional(),
			ORDER_NUMBER: z.instanceof(RegExp).optional(),
			PASSENGER_LAST_NAME_LATIN_REGEX: z.instanceof(RegExp).optional(),
			PASSENGER_LAST_NAME_RU_REGEX: z.instanceof(RegExp).optional()
		})
		.optional(),
	servicesAvailable: ServicesAvailableSchema.optional().describe(
		'Управление доступностью услуг на странице бронирования. animal - отображение карточки покупки услуги перевозки дом. животных'
	),
	stepsAvailable: StepsAvailableSchema.optional().describe('Управление доступностью шагов на бронировании'),
	upsaleServices: UpsaleServicesConfigSchema.optional().describe(
		'Определение сервисов, которые будут предлагаться пассажиру в модальном окне. См. Upsale Services'
	),
	roundMoneyAmount: BooleanOptional,
	currentUserInGlobalScope: BooleanOptional,
	purchasePredictionThreshold: NumberOptional.describe(
		'При переходе к оплате запрашиваем результаты предсказания по вероятности покупки доп. услуг. Если вероятность превышает или равна заданному значению, выводится попап с предложением добавить дополнительные услуги.'
	),
	purchasePredictionThresholdMax: NumberOptional.describe(
		'Максимальное значение вероятности покупки дополнительных услуг. Вероятность не должна превышать этот порог.'
	),
	telegramLoginBotId: StringOptional.describe('Айди телеграм бота'),
	socialAuthCallbackUrl: StringOptional.describe(
		'Используется в формировании backRedirectUrl, по дефолту url будет равен `хостнейм + /social-auth/smth` в конце. Например, https://flyredwings.com/social-auth/t-id\nЕсли указать значение `config.global.socialAuthCallbackUrl` эта строка будет использоваться вместо хостнейма\n\nWEBSKY-6437'
	),
	additionalResources: AdditionalResourcesSchema.optional().describe(
		'все скрипты и svg прописанные в этих массивах, будут подключаться в head сайт автоматически при начальном рендере приложения'
	),
	showLeavePageModal: BooleanOptional.describe(
		'Отображать модальное окно ухода со страницы (отображается в случае ухода курсора за границу окна активной вкладки)'
	),
	showCitiesPlaceholder: BooleanOptional.describe(
		'Отображение заглушки для картинок городов (только в модалке выбора тарифа)'
	),
	isOldConfirmOption: BooleanOptional.optional().describe(
		'При значение `true` компонент `ConfirmOption` имеет старый дизайн'
	),
	splitCartFlightPassengers: BooleanOptional.describe(
		'Разбивка стоимости перелета по пассажирам в секции "Полеты" в корзине'
	),
	showFareGroupUpgradeOptions: BooleanOptional,
	showBookingPreview: BooleanOptional,
	showTaxesInfo: BooleanOptional,
	alternativeRouting: BooleanOptional
});

export type GlobalConfig = z.infer<typeof GlobalConfigSchema>;
